import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { MonoTypeOperatorFunction, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { EditorFilesService, type JobStatusResponse } from './editor-files.service';
import { EditorStateService } from './editor-state.service';

export interface JobListItem {
	jobId: string;
	type: 'versions' | 'video-mask' | 'thumbnail';
	mediaId: string;
	mediaName: string;
	mediaCategory?: string;
	status: 'pending' | 'running' | 'done' | 'error';
	progress: number;
	currentStep: string;
	completedVersions: string[];
	totalVersions: number;
	error?: string;
	/** Per-item failures that didn't abort the job — present alongside status:'done', not just status:'error'. */
	errorMessages?: string[];
	createdAt: string;
	updatedAt: string;
}

const POLL_INTERVAL_MS = 2000;

/**
 * Central service for tracking version generation jobs across the editor.
 * - Loads active jobs on editor init (resumes polling after page reload)
 * - Single polling loop for all active jobs (efficient)
 * - Components read job state from signals, no local polling
 */
@Injectable()
export class EditorJobsService {
	private http = inject(HttpClient);
	private filesService = inject(EditorFilesService);
	private stateService = inject(EditorStateService);
	private messageService = inject(MessageService);

	/** All known jobs keyed by jobId (includes completed for dialog history). */
	readonly jobs = signal<Map<string, JobListItem>>(new Map());

	/** Active jobs keyed by mediaId — for quick lookup from components. */
	readonly activeByMediaId = computed(() => {
		const map = new Map<string, JobListItem>();
		for (const job of this.jobs().values()) {
			if (job.status === 'pending' || job.status === 'running') {
				map.set(job.mediaId, job);
			}
		}
		return map;
	});

	readonly activeCount = computed(() => this.activeByMediaId().size);

	private projectSlug = '';
	private pollTimer: ReturnType<typeof setInterval> | null = null;

	/** Load active jobs for the project and start polling. Called on editor init. */
	loadActiveJobs(projectSlug: string): void {
		this.projectSlug = projectSlug;
		this.http.get<JobListItem[]>(`${environment.apiUrl}/api/projects/${projectSlug}/media/jobs?status=active`).subscribe({
			next: (jobs) => {
				this.jobs.update(map => {
					const next = new Map(map);
					for (const job of jobs) next.set(job.jobId, job);
					return next;
				});
				if (jobs.length > 0) this.startPolling();
			},
		});
	}

	/** Load all recent jobs (for the queue dialog). */
	loadAllJobs(status: 'all' | 'active' | 'done' | 'error' = 'all'): void {
		if (!this.projectSlug) return;
		this.http.get<JobListItem[]>(`${environment.apiUrl}/api/projects/${this.projectSlug}/media/jobs?status=${status}&limit=100`).subscribe({
			next: (jobs) => {
				this.jobs.update(map => {
					const next = new Map(map);
					for (const job of jobs) next.set(job.jobId, job);
					return next;
				});
			},
		});
	}

	/** Register a freshly-started job and begin polling. */
	trackJob(job: Omit<JobListItem, 'createdAt' | 'updatedAt'>): void {
		const now = new Date().toISOString();
		const full: JobListItem = { ...job, createdAt: now, updatedAt: now };
		this.jobs.update(map => {
			const next = new Map(map);
			next.set(full.jobId, full);
			return next;
		});
		this.startPolling();
	}

	/**
	 * Pipe-able operator for `generateVersions`-style calls: registers a tracked
	 * job for the returned `{ jobId }` and wires `takeUntilDestroyed` so the
	 * fire-and-forget subscribe is cancelled if the caller's component dies
	 * before the request returns.
	 *
	 * Usage:
	 *   this.filesService.generateVersions(mediaId)
	 *     .pipe(this.jobsService.andTrack({ mediaId, mediaName, destroyRef: this.destroyRef }))
	 *     .subscribe();
	 */
	andTrack(meta: {
		mediaId: string;
		mediaName: string;
		mediaCategory?: string;
		destroyRef: DestroyRef;
	}): MonoTypeOperatorFunction<{ jobId: string; status: string }> {
		return (source) => source.pipe(
			takeUntilDestroyed(meta.destroyRef),
			tap(({ jobId }) => {
				this.trackJob({
					jobId, type: 'versions',
					mediaId: meta.mediaId,
					mediaName: meta.mediaName,
					mediaCategory: meta.mediaCategory,
					status: 'pending', progress: 0,
					currentStep: 'Queued', completedVersions: [], totalVersions: 0,
				});
			}),
		);
	}

	/** Get the active job for a media (if any). Used by components to show progress. */
	getJobForMedia(mediaId: string): JobListItem | undefined {
		return this.activeByMediaId().get(mediaId);
	}

	/** Get the most recent completed job for a media (if any). Used by effects to react to completion. */
	getCompletedJobForMedia(mediaId: string): JobListItem | undefined {
		for (const job of this.jobs().values()) {
			if (job.mediaId === mediaId && job.status === 'done') return job;
		}
		return undefined;
	}

	/** Cancel a running/pending job via API then remove from tracking. */
	cancelJob(jobId: string): void {
		this.http.post(`${environment.apiUrl}/api/media/jobs/${jobId}/cancel`, {}).subscribe({
			next: () => this.dismissJob(jobId),
			error: () => this.dismissJob(jobId),
		});
	}

	/** Remove a job from the map (used by dialog to dismiss completed entries). */
	dismissJob(jobId: string): void {
		this.jobs.update(map => {
			const next = new Map(map);
			next.delete(jobId);
			return next;
		});
	}

	private startPolling(): void {
		if (this.pollTimer) return;
		this.pollTimer = setInterval(() => this.pollActive(), POLL_INTERVAL_MS);
	}

	private stopPolling(): void {
		if (this.pollTimer) {
			clearInterval(this.pollTimer);
			this.pollTimer = null;
		}
	}

	private pollActive(): void {
		const active = this.activeByMediaId();
		if (active.size === 0) {
			this.stopPolling();
			return;
		}

		// Single API call returning all active jobs for this project
		this.http.get<JobListItem[]>(`${environment.apiUrl}/api/projects/${this.projectSlug}/media/jobs?status=active`).subscribe({
			next: (freshJobs) => {
				const prevActiveIds = new Set(Array.from(active.values()).map(j => j.jobId));
				const freshIds = new Set(freshJobs.map(j => j.jobId));

				// Update state: fresh jobs replace stale entries
				this.jobs.update(map => {
					const next = new Map(map);
					for (const job of freshJobs) next.set(job.jobId, job);
					return next;
				});

				// Jobs that were active but are no longer in the fresh response → completed
				const completedIds = [...prevActiveIds].filter(id => !freshIds.has(id));
				const prevJobsById = new Map(Array.from(active.values()).map(j => [j.jobId, j]));
				for (const jobId of completedIds) {
					const mediaName = prevJobsById.get(jobId)?.mediaName ?? 'Job';
					this.http.get<JobStatusResponse>(`${environment.apiUrl}/api/media/jobs/${jobId}`).subscribe({
						next: (finalStatus) => {
							this.jobs.update(m => {
								const nm = new Map(m);
								const existing = nm.get(jobId);
								if (existing) nm.set(jobId, { ...existing, ...finalStatus, updatedAt: new Date().toISOString() });
								return nm;
							});
							if (finalStatus.status === 'done') {
								// Reload files to pick up the canonical baseUrl, then mirror the
								// backend's post-gen URL canonicalization into open-form state so
								// stale extensions (e.g. `.png`) in the form can't clobber `.jpg` on save.
								const mediaIdForJob = finalStatus.mediaId ?? prevJobsById.get(jobId)?.mediaId;
								this.filesService.reloadAndThen(() => {
									if (!mediaIdForJob) return;
									const file = this.filesService.files().find(f => f.id === mediaIdForJob);
									const canonical = file?.baseUrl;
									if (canonical) this.stateService.canonicalizeMediaUrls(canonical);
									// Once playable versions exist for a video, drop the
									// VIDEO_NP placeholder on every scene that references it.
									if (canonical && (file?.category === 'video' || file?.category === 'video-mask')) {
										this.stateService.clearPlaybackTypeForMedia(canonical);
									}
									// DEV-128: auto-combine video + mask once both sides have
									// generated versions — mirrors clicking "Create Video Mask".
									if (canonical && (file?.category === 'video' || file?.category === 'mask')) {
										this.maybeAutoComposite(canonical, file.category);
									}
								});
								if (finalStatus.errorMessages?.length) {
									// Some items failed but the job still ran to completion —
									// distinct from the full-job 'error' case below, so this
									// doesn't get lost as a silent success.
									this.messageService.add({
										severity: 'warn',
										summary: 'Generation completed with errors',
										detail: `${mediaName} — ${finalStatus.completedVersions.length} generated, ${finalStatus.errorMessages.length} failed (${finalStatus.errorMessages[0]}${finalStatus.errorMessages.length > 1 ? ', ...' : ''})`,
										life: 8000,
									});
								} else {
									this.messageService.add({
										severity: 'success',
										summary: 'Generation complete',
										detail: `${mediaName} — ${finalStatus.completedVersions.length} version(s) generated`,
										life: 4000,
									});
								}
							} else if (finalStatus.status === 'error') {
								this.messageService.add({
									severity: 'error',
									summary: 'Generation failed',
									detail: `${mediaName} — ${finalStatus.error ?? 'Unknown error'}`,
									life: 6000,
								});
							}
						},
					});
				}
			},
			error: () => { /* skip this poll; will retry next tick */ },
		});
	}

	/**
	 * DEV-128: after a video/mask media finishes generating versions, check
	 * whether any loaded scene pairs it with a mask/video that's already
	 * generated too — if so, auto-fire the composite (same call `startComposite()`
	 * makes in scene-form.ts). No dedup guard for the rare case where both
	 * sides finish in the same poll tick and each fires the composite — the
	 * backend reuses/overwrites the same composite media doc on re-run.
	 */
	private maybeAutoComposite(mediaBaseUrl: string, side: 'video' | 'mask'): void {
		const pairedUrls = this.stateService.findPairedMediaBaseUrl(mediaBaseUrl, side);
		if (!pairedUrls.length) return;

		const thisMedia = this.filesService.findByBaseUrl(mediaBaseUrl);
		if (!thisMedia?.id) return;

		for (const pairedUrl of pairedUrls) {
			const pairedMedia = this.filesService.findByBaseUrl(pairedUrl);
			if (!pairedMedia?.id || !pairedMedia.isLocal || (pairedMedia.versionCount ?? 0) <= 1) continue;

			const videoId = side === 'video' ? thisMedia.id : pairedMedia.id;
			const maskId = side === 'mask' ? thisMedia.id : pairedMedia.id;
			const videoBaseUrl = side === 'video' ? mediaBaseUrl : pairedUrl;
			const maskBaseUrl = side === 'mask' ? mediaBaseUrl : pairedUrl;
			this.filesService.createVideoMask(videoId, maskId).subscribe(({ jobId, newMediaId }) => {
				this.trackJob({
					jobId, type: 'video-mask', mediaId: newMediaId,
					mediaName: 'video-mask composite',
					mediaCategory: 'video-mask',
					status: 'pending', progress: 0,
					currentStep: 'Queued', completedVersions: [], totalVersions: 0,
				});
				// Lets the open scene-form (if any) mirror the "Queued" indicator
				// under its own button — it has no other way to learn this job's
				// mediaId since it wasn't the one that fired the request.
				this.stateService.notifyVideoMaskComposite(videoBaseUrl, maskBaseUrl, jobId, newMediaId);
			});
		}
	}

	destroy(): void {
		this.stopPolling();
	}
}
